import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import authEn from '../locales/en/auth.json';
import sidebarEn from '../locales/en/sidebar.json';
import accountEn from '../locales/en/account.json';
import orgSettingsEn from '../locales/en/orgSettings.json';
import subscriptionEn from '../locales/en/subscription.json';
import usersEn from '../locales/en/users.json';
import commonEn from '../locales/en/common.json';
import projectEn from '../locales/en/project.json';
import componentsEn from '../locales/en/components.json';
import onboardingEn from '../locales/en/onboarding.json';
import featureGateEn from '../locales/en/featureGate.json';
// The map sheet composer's strings live in their own namespace and are typed
// loosely below: every typed key adds to the key unions i18next builds for
// `t`, and at the size the typed bundles have reached, a little more makes
// tsc crash ("Debug Failure. No error for last overload signature") wherever
// a typed `t` is passed to a plain `(key: string) => string` parameter.
// `mapExportI18n.test.ts` checks these keys in all five locales instead.
import mapSheetEn from '../locales/en/mapSheet.json';
// Findings strings live in their own namespace and are typed loosely below:
// every typed key adds to the key unions i18next builds for `t`, and the
// typed bundles are at the size where a little more makes tsc crash ("Debug
// Failure. No error for last overload signature"). `findingsI18n.test.ts`
// checks these keys in all five locales instead.
import findingsEn from '../locales/en/findings.json';
// Accepted clearance intervals: own namespace, loosely typed for the same
// reason; `clearanceAcceptI18n.test.ts` checks its keys in all five locales.
import clearanceAcceptEn from '../locales/en/clearanceAccept.json';
// The clearance dock's top-view strip, same reason; `clearanceStripI18n.test.ts`.
import clearanceStripEn from '../locales/en/clearanceStrip.json';
// Typed clip box / surface parameters (GIS-442/443), same reason; `numericEditI18n.test.ts`.
import numericEditEn from '../locales/en/numericEdit.json';
// The stockpile PDF report (buttons, toasts, page text), same reason; `stockpileReportI18n.test.ts`.
import stockpileReportEn from '../locales/en/stockpileReport.json';
// Branding strings every browser-built PDF report shares (Powered by / Prepared by / Prepared on), same reason; `pdfReportI18n.test.ts`.
import pdfReportEn from '../locales/en/pdfReport.json';
// The deviation value grid's card entry and dock, same reason; `valueGridI18n.test.ts`.
import valueGridEn from '../locales/en/valueGrid.json';

const supportedLngs = ['en', 'es', 'de', 'fr', 'hu'] as const;

export const defaultNS = 'auth' as const;
export const resources = {
  en: {
    auth: authEn,
    sidebar: sidebarEn,
    account: accountEn,
    orgSettings: orgSettingsEn,
    subscription: subscriptionEn,
    users: usersEn,
    common: commonEn,
    project: projectEn,
    components: componentsEn,
    onboarding: onboardingEn,
    featureGate: featureGateEn,
    mapSheet: mapSheetEn as Record<string, unknown>,
    findings: findingsEn as Record<string, unknown>,
    clearanceAccept: clearanceAcceptEn as Record<string, unknown>,
    clearanceStrip: clearanceStripEn as Record<string, unknown>,
    numericEdit: numericEditEn as Record<string, unknown>,
    stockpileReport: stockpileReportEn as Record<string, unknown>,
    pdfReport: pdfReportEn as Record<string, unknown>,
    valueGrid: valueGridEn as Record<string, unknown>,
  },
} as const;

export type SupportedLanguage = (typeof supportedLngs)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use({
    type: 'backend' as const,
    read(language: string, namespace: string, callback: (err: Error | null, data: Record<string, string> | null) => void) {
      const lang = supportedLngs.includes(language as SupportedLanguage) ? language : 'en';
      import(`../locales/${lang}/${namespace}.json`)
        .then((module) => callback(null, module.default))
        .catch((error) => callback(error, null));
    },
  })
  .init({
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    supportedLngs,
    fallbackLng: 'en',
    // React escapes every string it renders, so i18next escaping on top only
    // corrupts output: "Crest / toe line" printed as "Crest &#x2F; toe line",
    // "Cut & fill" as "Cut &amp; fill" (field report, 2026-10-24). This is the
    // react-i18next recommended setting; nothing passes t() output to innerHTML.
    interpolation: { escapeValue: false },
    nonExplicitSupportedLngs: true,
    debug: import.meta.env.DEV,
    defaultNS: 'auth',
    ns: ['auth', 'sidebar', 'account', 'orgSettings', 'subscription', 'users', 'common', 'project', 'components', 'onboarding', 'featureGate', 'mapSheet', 'findings', 'clearanceAccept', 'clearanceStrip', 'numericEdit', 'stockpileReport', 'pdfReport', 'valueGrid'],
    react: {
      useSuspense: true,
      // Re-render on resource-bundle changes so the dev locale hot-swap
      // below (and lazy namespace loads) show up without a language change.
      bindI18nStore: 'added removed',
    },
  });

// Dev-only: localeHmrPlugin (vite.config.ts) intercepts locale JSON edits and
// ships the fresh translations over this event instead of letting HMR
// propagate to main.tsx (which would force a full page reload).
if (import.meta.hot) {
  import.meta.hot.on('locale-update', ({ lang, ns, resources }: { lang: string; ns: string; resources: Record<string, unknown> }) => {
    i18n.removeResourceBundle(lang, ns);
    i18n.addResourceBundle(lang, ns, resources, true, true);
  });
}

export const languageNames: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  hu: 'Magyar',
};

export { supportedLngs };
export default i18n;
